import * as React from 'react'

import { LoaderIcon } from '@/app/features/icons/Loader'
import { Portal } from '@/ui/overlays/Portal'


const TOAST_OFFSET_VAR = '--et-task-banner-offset'

let claims: Array<object> = []
const claimListeners = new Set<() => void>()

const notifyClaims = () => claimListeners.forEach((listener) => listener())

const subscribeToClaims = (listener: () => void) => {
  claimListeners.add(listener)
  return () => {
    claimListeners.delete(listener)
  }
}

const readPrimaryClaim = () => claims[0]

const usePrimaryClaim = (): boolean => {
  const claim = React.useRef<object>({}).current
  const primary = React.useSyncExternalStore(
    subscribeToClaims,
    readPrimaryClaim,
  )

  React.useEffect(() => {
    claims = [...claims, claim]
    notifyClaims()

    return () => {
      claims = claims.filter((entry) => entry !== claim)
      notifyClaims()
    }
  }, [claim])

  return primary === claim
}

type TaskStatusBannerProps = {
  label: string
  detail?: string
  percent?: number | null
}


export const TaskStatusBanner: React.FC<TaskStatusBannerProps> = ({
  label,
  detail,
  percent = null,
}) => {
  const isPrimary = usePrimaryClaim()
  const cardRef = React.useRef<HTMLDivElement | null>(null)

  React.useLayoutEffect(() => {
    if (!isPrimary) return undefined

    const root = document.documentElement
    const publishHeight = () => {
      root.style.setProperty(
        TOAST_OFFSET_VAR,
        `${cardRef.current?.offsetHeight ?? 0}px`,
      )
    }

    publishHeight()

    const observer =
      typeof ResizeObserver === 'undefined' || !cardRef.current
        ? null
        : new ResizeObserver(publishHeight)
    if (observer && cardRef.current) observer.observe(cardRef.current)

    return () => {
      observer?.disconnect()
      root.style.removeProperty(TOAST_OFFSET_VAR)
    }
  }, [isPrimary])

  if (!isPrimary) return null

  const hasPercent = percent !== null && Number.isFinite(percent)
  const clampedPercent = hasPercent
    ? Math.min(100, Math.max(0, Math.round(percent as number)))
    : 0

  return (
    <Portal layer="toast">
      <div
        ref={cardRef}
        className="tw-pointer-events-none tw-fixed tw-right-3 tw-top-3 tw-z-[10060] tw-p-3"
        role="status"
        aria-live="polite"
        aria-atomic="false"
      >
        <div className="et-task-banner-in tw-w-[340px] tw-max-w-[calc(100vw-32px)] tw-overflow-hidden tw-rounded-xl tw-border tw-border-base-300 tw-bg-base-100 tw-shadow-multi">
          <div className="tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3">
            <span className="et-task-banner-badge tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full">
              <LoaderIcon className="tw-h-[18px] tw-w-[18px] tw-animate-spin" />
            </span>

            <div className="tw-min-w-0 tw-flex-1">
              {/* Dwie linie zamiast obciecia: dluzsze tlumaczenia nazwy operacji nie
                  mieszcza sie w jednej, a to wlasnie ta linia mowi, co sie dzieje. */}
              <p className="tw-line-clamp-2 tw-text-sm tw-font-semibold tw-leading-tight tw-text-content-base">
                {label}
              </p>
              {detail ? (
                <p
                  // Zegar tyka co sekunde - czytnik ekranu dostaje sama nazwe operacji,
                  // inaczej powtarzalby caly komunikat w kolko.
                  aria-hidden
                  className="tw-mt-1 tw-truncate tw-text-xs tw-leading-tight tw-text-neutral"
                >
                  {detail}
                </p>
              ) : null}
            </div>

            {hasPercent ? (
              <span
                aria-hidden
                className="tw-shrink-0 tw-text-sm tw-font-semibold tw-tabular-nums tw-text-primary"
              >
                {clampedPercent}%
              </span>
            ) : null}
          </div>

          <div className="tw-relative tw-h-1 tw-w-full tw-overflow-hidden tw-bg-base-300">
            {hasPercent ? (
              <div
                className="et-task-progress-bar tw-h-full tw-transition-[width] tw-duration-500 tw-ease-out"
                style={{ width: `${clampedPercent}%` }}
              />
            ) : (
    
              <div className="et-task-progress-bar et-task-progress-sweep tw-absolute tw-inset-y-0 tw-w-1/3 tw-rounded-full" />
            )}
          </div>
        </div>
      </div>
    </Portal>
  )
}

export default TaskStatusBanner
